import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { systemAPI } from './services/api';

// Components (always loaded)
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import InvitationGate from './components/InvitationGate';

// Lazy-loaded Pages – each becomes a separate JS chunk
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ContestList = lazy(() => import('./pages/ContestList'));
const ContestDetail = lazy(() => import('./pages/ContestDetail'));
const ProblemSolve = lazy(() => import('./pages/ProblemSolve'));

function ProblemSolveWrapper() {
  const { problemId } = useParams();
  return <ProblemSolve key={problemId} />;
}
const Standings = lazy(() => import('./pages/Standings'));
const SubmissionHistory = lazy(() => import('./pages/SubmissionHistory'));
const SubmissionDetail = lazy(() => import('./pages/SubmissionDetail'));
const Profile = lazy(() => import('./pages/Profile'));

// Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const CreateContest = lazy(() => import('./pages/admin/CreateContest'));
const ManageProblems = lazy(() => import('./pages/admin/ManageProblems'));
const CreateProblem = lazy(() => import('./pages/admin/CreateProblem'));
const ViewSubmissions = lazy(() => import('./pages/admin/ViewSubmissions'));
const AICreateProblem = lazy(() => import('./pages/admin/AICreateProblem'));
const ManageInvitations = lazy(() => import('./pages/admin/ManageInvitations'));
const GroupStats = lazy(() => import('./pages/admin/GroupStats'));
const ManageBanner = lazy(() => import('./pages/admin/ManageBanner'));
const TeacherDashboard = lazy(() => import('./pages/teacher/TeacherDashboard'));
const TeacherGroups = lazy(() => import('./pages/teacher/TeacherGroups'));
const TeacherCreateContest = lazy(() => import('./pages/teacher/TeacherCreateContest'));
const TeacherEditContest = lazy(() => import('./pages/teacher/TeacherEditContest'));
const TeacherContestProblems = lazy(() => import('./pages/teacher/TeacherContestProblems'));
const TeacherInvitationStats = lazy(() => import('./pages/teacher/TeacherInvitationStats'));

// Lightweight fallback for Suspense (shown while chunks load)
const PageSpinner = () => (
  <div className="flex justify-center items-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
  </div>
);

const BANNER_CACHE_KEY = 'banner_cache';
const BANNER_CACHE_TTL = 60_000; // 1 minute

function GlobalBanner() {
  const location = useLocation();
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const fetchBanner = async () => {
      // Check sessionStorage cache first
      try {
        const cached = sessionStorage.getItem(BANNER_CACHE_KEY);
        if (cached) {
          const { text, ts } = JSON.parse(cached);
          if (Date.now() - ts < BANNER_CACHE_TTL) {
            if (text) { setMessage(text); setVisible(true); timer = setTimeout(() => { if (!cancelled) setVisible(false); }, 3000); }
            else { setVisible(false); setMessage(''); }
            return;
          }
        }
      } catch { /* ignore corrupt cache */ }

      try {
        const res = await systemAPI.getBanner();
        if (cancelled) return;

        const text = (res.data?.banner?.message || '').trim();
        sessionStorage.setItem(BANNER_CACHE_KEY, JSON.stringify({ text, ts: Date.now() }));

        if (!text) {
          setVisible(false);
          setMessage('');
          return;
        }

        setMessage(text);
        setVisible(true);
        timer = setTimeout(() => {
          if (!cancelled) setVisible(false);
        }, 3000);
      } catch {
        if (!cancelled) {
          setVisible(false);
          setMessage('');
        }
      }
    };

    fetchBanner();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname]);

  if (!visible || !message) return null;

  return (
    <div className="w-full bg-amber-100 border-b border-amber-300 text-amber-900 text-center text-sm font-medium py-2 px-4">
      {message}
    </div>
  );
}

function AppRoutes() {
  const { user, loading, activated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show invitation gate for logged-in but non-activated students
  if (user && !activated) {
    return <InvitationGate />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {user && <Navbar />}
      <GlobalBanner />
      <main className={user ? 'pt-16' : ''}>
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />

          {/* Student Routes */}
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/contests" element={<ProtectedRoute><ContestList /></ProtectedRoute>} />
          <Route path="/contests/:id" element={<ProtectedRoute><ContestDetail /></ProtectedRoute>} />
          <Route path="/contests/:contestId/problems/:problemId" element={<ProtectedRoute><ProblemSolveWrapper /></ProtectedRoute>} />
          <Route path="/standings/:contestId" element={<ProtectedRoute><Standings /></ProtectedRoute>} />
          <Route path="/submissions" element={<ProtectedRoute><SubmissionHistory /></ProtectedRoute>} />
          <Route path="/submissions/:id" element={<ProtectedRoute><SubmissionDetail /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/groups" element={<ProtectedRoute><InvitationGate asPage /></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/contests/create" element={<ProtectedRoute adminOnly><CreateContest /></ProtectedRoute>} />
          <Route path="/admin/contests/:id/edit" element={<ProtectedRoute adminOnly><CreateContest /></ProtectedRoute>} />
          <Route path="/admin/contests/:contestId/problems" element={<ProtectedRoute adminOnly><ManageProblems /></ProtectedRoute>} />
          <Route path="/admin/contests/:contestId/problems/create" element={<ProtectedRoute adminOnly><CreateProblem /></ProtectedRoute>} />
          <Route path="/admin/contests/:contestId/problems/:problemId/edit" element={<ProtectedRoute adminOnly><CreateProblem /></ProtectedRoute>} />
          <Route path="/admin/contests/:contestId/problems/ai" element={<ProtectedRoute adminOnly><AICreateProblem /></ProtectedRoute>} />
          <Route path="/admin/submissions" element={<ProtectedRoute adminOnly><ViewSubmissions /></ProtectedRoute>} />
          <Route path="/admin/invitations" element={<ProtectedRoute adminOnly><ManageInvitations /></ProtectedRoute>} />
          <Route path="/admin/banner" element={<ProtectedRoute adminOnly><ManageBanner /></ProtectedRoute>} />
          <Route path="/admin/invitations/:codeId/stats" element={<ProtectedRoute adminOnly><GroupStats /></ProtectedRoute>} />

          {/* Teacher Routes */}
          <Route path="/teacher" element={<ProtectedRoute teacherOnly><TeacherDashboard /></ProtectedRoute>} />
          <Route path="/teacher/groups" element={<ProtectedRoute teacherOnly><TeacherGroups /></ProtectedRoute>} />
          <Route path="/teacher/contests/create" element={<ProtectedRoute teacherOnly><TeacherCreateContest /></ProtectedRoute>} />
          <Route path="/teacher/contests/:id/edit" element={<ProtectedRoute teacherOnly><TeacherEditContest /></ProtectedRoute>} />
          <Route path="/teacher/contests/:contestId/problems" element={<ProtectedRoute teacherOnly><TeacherContestProblems /></ProtectedRoute>} />
          <Route path="/teacher/contests/:contestId/problems/create" element={<ProtectedRoute teacherOnly><CreateProblem teacherMode /></ProtectedRoute>} />
          <Route path="/teacher/contests/:contestId/problems/:problemId/edit" element={<ProtectedRoute teacherOnly><CreateProblem teacherMode /></ProtectedRoute>} />
          <Route path="/teacher/invitations/:codeId/stats" element={<ProtectedRoute teacherOnly><TeacherInvitationStats /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </Suspense>
      </main>
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
